/** @jsxImportSource @emotion/react */
import {
  EuiTable,
  EuiTableBody,
  EuiTableHeader,
  LEFT_ALIGNMENT,
  RIGHT_ALIGNMENT,
  EuiText,
  EuiBadge,
} from "@inscopix/ideas-eui";
import { DataTableTaskCounts } from "pages/gdt//layout/DataTableTaskCounts";
import { formatDate } from "utils/formatDate";
import { css } from "@emotion/react";
import { UserAvatar } from "components/UserAvatar/UserAvatar";
import { ProjectTablesTableHeaders } from "./ProjectTablesTableHeaders";
import { ProjectTablesTableRows } from "./ProjectTablesTableRows";
import { ProjectDataTable } from "components/ProjectTablesTable/provider/ProjectTablesTableProvider.types";
import { ProjectTablesTableColumn } from "components/ProjectTablesTable/ProjectTablesTable.types";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";

export const ProjectTablesTableColumns: ProjectTablesTableColumn[] = [
  {
    id: "name",
    label: "Name",
    alignment: LEFT_ALIGNMENT,
    isSortable: true,
    isHidable: false,
  },
  {
    id: "tasks",
    label: "Stats",
    isVisuallyHiddenLabel: true,
    isHidable: true,
    alignment: RIGHT_ALIGNMENT,
    width: "100px",
    render: (table: ProjectDataTable) =>
      table.kind === "analysis" ? (
        <DataTableTaskCounts tableId={table.id} />
      ) : null,
  },
  {
    id: "date_created",
    label: "Created",
    isSortable: true,
    isHidable: true,
    defaultVisibility: false,
    alignment: LEFT_ALIGNMENT,
    width: "150px",
    sortType: "date",
    render: (table: ProjectDataTable) => {
      return (
        <EuiText size="s" color="subdued">
          {formatDate(table.date_created, "YYYY-MM-DD HH:mm")}
        </EuiText>
      );
    },
  },
  {
    id: "date_updated",
    label: "Updated",
    isSortable: true,
    isHidable: true,
    alignment: LEFT_ALIGNMENT,
    width: "150px",
    sortType: "date",
    render: (table: ProjectDataTable) => {
      return (
        <EuiText size="s" color="subdued">
          {formatDate(table.date_updated, "YYYY-MM-DD HH:mm")}
        </EuiText>
      );
    },
  },
  {
    id: "kind",
    label: "Type",
    isVisuallyHiddenLabel: true,
    isSortable: true,
    isHidable: false,
    defaultVisibility: false,
    alignment: LEFT_ALIGNMENT,
    width: "100px",
    render: (table: ProjectDataTable) => (
      <EuiBadge color={table.kind === "analysis" ? "hollow" : "default"}>
        {table.kind === "analysis" ? "Analysis" : "Data"}
      </EuiBadge>
    ),
  },
  {
    id: "user",
    label: "User",
    isVisuallyHiddenLabel: true,
    isSortable: true,
    isHidable: true,
    defaultVisibility: false,
    width: "32px",
    render: (table: ProjectDataTable) => (
      <UserAvatar userId={table.user} size="s" />
    ),
  },
  {
    id: "actions",
    label: "Actions",
    isHidable: false,
    alignment: RIGHT_ALIGNMENT,
    isActionsPopover: true,
    width: "32px",
  },
];

// only show row checkboxes on hover, replace icon with checkbox
const styles = css`
  .table-row {
    .table-checkbox {
      display: none;
      vertical-align: text-top;
    }
  }
  .table-row.hidden .euiTableRowCell {
    opacity: 0.5;
  }
  &.editable .table-row {
    &:hover {
      .table-checkbox {
        display: inline-block;
      }
      .table-icon {
        display: none;
      }
    }
    .selected {
      .table-checkbox {
        display: inline-block;
      }
      .table-icon {
        display: none;
      }
    }
  }
`;

/*
 * Displays a table of project tables
 */
export const ProjectTablesTable = () => {
  const canEdit = useProjectPermissionStore((s) => s.permissions.edit);

  return (
    <EuiTable responsive={false}>
      <EuiTableHeader>
        <ProjectTablesTableHeaders />
      </EuiTableHeader>
      <EuiTableBody css={styles} className={canEdit ? "editable" : undefined}>
        <ProjectTablesTableRows />
      </EuiTableBody>
    </EuiTable>
  );
};
